import React, { useState } from "react";
import { PropertyValuationData } from "../../../types/valuation";
import { useTenant } from "../../../contexts/TenantContext";
import { usePropertyPhotosQuery } from "../../../hooks/valuation/usePropertyPhotosQuery";

interface PhotoGalleryProps {
  property: PropertyValuationData;
  className?: string;
}

export const PhotoGallery: React.FC<PhotoGalleryProps> = ({
  property,
  className = "",
}) => {
  const { config } = useTenant();
  const colors = config?.branding?.colors || {
    primary: "#000000",
    secondary: "#000000",
    accent: "#000000",
    background: "#ffffff",
    text: "#000000",
  };
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const { mainPhotos, isLoading } = usePropertyPhotosQuery(property);
  const [imageLoading, setImageLoading] = useState(false);

  if (isLoading) {
    return (
      <div
        className={`${className} h-[350px] rounded-xl animate-pulse`}
        style={{ backgroundColor: `${colors.background}10` }}
      />
    );
  }

  if (!mainPhotos) {
    return (
      <div
        className={`${className} h-[350px] rounded-xl flex flex-col items-center justify-center`}
        style={{ backgroundColor: `${colors.background}10` }}
      >
        <svg
          className="h-24 w-24 mb-4"
          fill="none"
          stroke={colors.text}
          style={{ opacity: 0.4 }}
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1"
            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
          />
        </svg>
        {property.propertyInfo.address?.address && (
          <p className="text-sm" style={{ color: `${colors.text}99` }}>
            {property.propertyInfo.address.address}
          </p>
        )}
      </div>
    );
  }

  const allPhotos = (() => {
    if (mainPhotos.type === "valuation" && mainPhotos.additionalUrls) {
      // Filter out any invalid URLs
      const validUrls = [
        mainPhotos.primaryUrl,
        ...mainPhotos.additionalUrls,
      ].filter((url) => {
        try {
          new URL(url);
          return true;
        } catch {
          console.warn("Invalid photo URL detected:", url);
          return false;
        }
      });
      return validUrls.length > 0 ? validUrls : [mainPhotos.primaryUrl];
    }
    return [mainPhotos.primaryUrl];
  })();

  const nextPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev + 1) % allPhotos.length);
  };

  const previousPhoto = () => {
    setCurrentPhotoIndex(
      (prev) => (prev - 1 + allPhotos.length) % allPhotos.length
    );
  };

  return (
    <div
      className={`${className} relative h-[200px] lg:h-[350px] rounded-xl overflow-hidden group`}
    >
      <div className="relative w-full h-full">
        {imageLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-900 border-t-transparent" />
          </div>
        )}
        <img
          src={allPhotos[currentPhotoIndex]}
          alt={`Property photo ${currentPhotoIndex + 1}`}
          className={`w-full h-full object-cover transition-opacity duration-300 ${
            imageLoading ? "opacity-0" : "opacity-100"
          }`}
          onLoadStart={() => setImageLoading(true)}
          onLoad={() => setImageLoading(false)}
          onError={(e) => {
            setImageLoading(false);
            console.error(
              "Failed to load photo:",
              allPhotos[currentPhotoIndex]
            );
            e.currentTarget.src = "/assets/images/place-holder.webp";
          }}
        />

        {/* Navigation arrows - only show for multiple valuation photos */}
        {mainPhotos.type === "valuation" && allPhotos.length > 1 && (
          <>
            <button
              onClick={previousPhoto}
              className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Previous photo"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <button
              onClick={nextPhoto}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Next photo"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </>
        )}

        {/* Photo source indicator */}
        {mainPhotos.type !== "placeholder" && (
          <div className="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-sm">
            {(() => {
              switch (mainPhotos.type) {
                case "valuation":
                  return allPhotos.length > 1
                    ? `${currentPhotoIndex + 1} / ${allPhotos.length}`
                    : "Property Photo";
                case "streetview":
                  return "Street View";
                default:
                  return null;
              }
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoGallery;
